import { memo, useMemo, useState } from 'react'
import { MessageRole } from '@multiplayer-app/ai-agent-types'
import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'
import { useMessagesInfinite } from '@/hooks/useMessagesInfinite'
import { getChatState } from '@/state/useAgentStore'
import MessageGroup from '../MessageGroup'

interface SubagentChatPreviewProps {
  chatId: string
  isExpanded: boolean
}

export const SubagentChatPreview = memo(({ chatId, isExpanded }: SubagentChatPreviewProps) => {
  const runtime = useAgentRuntime()
  const storeMessages = useAgentStore((state) => getChatState(state, chatId).messages)
  const pendingAssistant = useAgentStore((state) => getChatState(state, chatId).pendingAssistant)
  const contextKey = useAgentStore((state) => state.chats[chatId]?.contextKey ?? state.contextKey)

  // Fetch lazily: previews that were never opened don't hit the server, but
  // once opened the data stays cached so collapse/expand is instant.
  const [hasEverExpanded, setHasEverExpanded] = useState(isExpanded)
  if (isExpanded && !hasEverExpanded) setHasEverExpanded(true)
  const messagesQuery = useMessagesInfinite(chatId, { requireRegisteredChat: false, enabled: hasEverExpanded })

  const messages = useMemo(() => {
    const sorted = [...storeMessages].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    if (runtime.config.debug) return sorted
    const firstUserIndex = sorted.findIndex((m) => m.role === MessageRole.User)
    if (firstUserIndex === -1) return sorted
    return sorted.filter((_, i) => i !== firstUserIndex)
  }, [storeMessages, runtime.config.debug])

  if (!isExpanded) return null

  return (
    <div className='mp-agent-subagent-preview'>
      <div className='mp-agent-subagent-preview-body'>
        {messages.length === 0 && !pendingAssistant ? (
          <div className='mp-agent-subagent-preview-empty'>
            {messagesQuery.isLoading ? 'Loading subagent chat...' : 'Subagent chat started.'}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageGroup key={message.id} message={message} contextKey={contextKey} showParticipantChrome />
            ))}
            {pendingAssistant && (
              <MessageGroup message={pendingAssistant} contextKey={contextKey} isStreaming showParticipantChrome />
            )}
          </>
        )}
      </div>
    </div>
  )
})

SubagentChatPreview.displayName = 'SubagentChatPreview'
